import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

import type { MatchMetric } from "./MatchMetric";

type MetricHint = {
  // Что измеряет метрика — строкой в карточке.
  meaning: MessageDescriptor;
  // Чем плохо низкое значение — в подсказке.
  why: MessageDescriptor;
  // Как исправить, подробнее короткого совета `METRIC_TIPS`, — в подсказке.
  fix: MessageDescriptor;
};

// Пояснения к метрикам матча. `Record` по union из контракта: новая метрика не соберётся
// без пояснения.
export const METRIC_HINTS: Record<MatchMetric, MetricHint> = {
  souls_per_min: {
    meaning: msg({
      id: "analysis.hint.souls_per_min.meaning",
      message:
        "All souls you earned in the match — troopers, camps, heroes, objectives, crates — per minute.",
    }),
    why: msg({
      id: "analysis.hint.souls_per_min.why",
      message:
        "Souls are items. Fewer souls mean later core items, so in fights you are weaker than enemies at the same minute.",
    }),
    fix: msg({
      id: "analysis.hint.souls_per_min.fix",
      message:
        "Don't idle between fights: clear camps near your lane, break crates on the way, last-hit troopers. While enemies are dead or far away, farm instead of waiting at a tower.",
    }),
  },
  neutral_damage_per_min: {
    meaning: msg({
      id: "analysis.hint.neutral_damage_per_min.meaning",
      message: "How much damage you dealt to neutral camps — how often you farmed the jungle.",
    }),
    why: msg({
      id: "analysis.hint.neutral_damage_per_min.why",
      message:
        "Camps are the second source of souls after the lane. Without them your income is capped by trooper waves.",
    }),
    fix: msg({
      id: "analysis.hint.neutral_damage_per_min.fix",
      message:
        "When your lane is pushed and no enemies are in sight, clear the nearest camp and come back for the next wave. Start with small camps, take big ones once you have the damage.",
    }),
  },
  buffs_per_min: {
    meaning: msg({
      id: "analysis.hint.buffs.meaning",
      message:
        "Bonuses to health, damage, fire rate and other stats from crates and statues around the map. They last until the end of the match.",
    }),
    why: msg({
      id: "analysis.hint.buffs.why",
      message:
        "Each bonus is small, but over a match they add up to a real edge — almost for free.",
    }),
    fix: msg({
      id: "analysis.hint.buffs.fix",
      message:
        "Break the crates and statues you pass: on the way to lane, between camps, while retreating. Learn where they stand around your lane.",
    }),
  },
  first_buff_time: {
    meaning: msg({
      id: "analysis.hint.first_buff_time.meaning",
      message: "When you picked up your first permanent bonus from a crate or statue.",
    }),
    why: msg({
      id: "analysis.hint.first_buff_time.why",
      message:
        "The earlier you get it, the longer it works for you — especially in lane, where every bit of health matters.",
    }),
    fix: msg({
      id: "analysis.hint.first_buff_time.fix",
      message:
        "On the way to lane and in the first minutes, while the trooper wave is far, break the crates near your lane.",
    }),
  },
  cs_share: {
    meaning: msg({
      id: "analysis.hint.cs_share.meaning",
      message: "Of the troopers that died near you, the share you last-hit yourself.",
    }),
    why: msg({
      id: "analysis.hint.cs_share.why",
      message:
        "Last hits are your main income in lane. Every missed trooper is souls you didn't get and items you buy later.",
    }),
    fix: msg({
      id: "analysis.hint.cs_share.fix",
      message:
        "Shoot a trooper when it is low instead of spraying the whole wave. Watch the troopers your own troopers are hitting — they die first.",
    }),
  },
  hero_damage_per_min: {
    meaning: msg({
      id: "analysis.hint.hero_damage_per_min.meaning",
      message: "Bullet and ability damage you dealt to enemy heroes per minute.",
    }),
    why: msg({
      id: "analysis.hint.hero_damage_per_min.why",
      message:
        "Low hero damage means you are too far from fights or not shooting and casting enough — your team fights without you.",
    }),
    fix: msg({
      id: "analysis.hint.hero_damage_per_min.fix",
      message:
        "Stay in fights at your weapon's range, not behind everyone. Use abilities as soon as they are ready and finish wounded enemies.",
    }),
  },
  accuracy: {
    meaning: msg({
      id: "analysis.hint.accuracy.meaning",
      message: "The share of your shots that hit a target.",
    }),
    why: msg({
      id: "analysis.hint.accuracy.why",
      message:
        "Misses are lost damage and extra reloads. With low accuracy even strong weapon items work at half power.",
    }),
    fix: msg({
      id: "analysis.hint.accuracy.fix",
      message:
        "Shoot from the range where your weapon is accurate. If tracking is hard, aim at the body, not the head. Don't spray from far away while running.",
    }),
  },
  crit_rate: {
    meaning: msg({
      id: "analysis.hint.crit_rate.meaning",
      message: "The share of your hits on heroes that landed on the head.",
    }),
    why: msg({
      id: "analysis.hint.crit_rate.why",
      message: "A headshot deals much more damage. Few headshots mean damage left on the table.",
    }),
    fix: msg({
      id: "analysis.hint.crit_rate.fix",
      message:
        "Keep your crosshair at head height when walking the lane and peeking corners. Against slow and large heroes always aim at the head.",
    }),
  },
  objective_damage_per_min: {
    meaning: msg({
      id: "analysis.hint.objective_damage_per_min.meaning",
      message: "Damage you dealt to enemy buildings and bosses per minute.",
    }),
    why: msg({
      id: "analysis.hint.objective_damage_per_min.why",
      message:
        "Matches are won by buildings, not kills. Without pressure after a won fight, enemies just respawn and the advantage is gone.",
    }),
    fix: msg({
      id: "analysis.hint.objective_damage_per_min.fix",
      message:
        "After a won fight or while enemies are dead, hit the nearest building together with your troopers. Don't spend that time farming the jungle.",
    }),
  },
  kill_participation: {
    meaning: msg({
      id: "analysis.hint.kill_participation.meaning",
      message: "The share of your team's kills where you got the kill or an assist.",
    }),
    why: msg({
      id: "analysis.hint.kill_participation.why",
      message: "Fights happen without you, so your team fights outnumbered.",
    }),
    fix: msg({
      id: "analysis.hint.kill_participation.fix",
      message:
        "Check the minimap: when allies start a fight nearby, join them. Farm closer to your allies so you make it to fights in time.",
    }),
  },
  parry_rate: {
    meaning: msg({
      id: "analysis.hint.parry_rate.meaning",
      message: "The share of your parries that blocked a melee hit.",
    }),
    why: msg({
      id: "analysis.hint.parry_rate.why",
      message:
        "A successful parry stuns the enemy and cancels their hit. A missed one leaves you open until it is ready again.",
    }),
    fix: msg({
      id: "analysis.hint.parry_rate.fix",
      message:
        "Parry at the moment of the hit, not early: watch for the heavy melee wind-up. Against melee heroes keep parry ready instead of using it at random.",
    }),
  },
  damage_taken_per_min: {
    meaning: msg({
      id: "analysis.hint.damage_taken_per_min.meaning",
      message: "Damage enemy heroes dealt to you per minute.",
    }),
    why: msg({
      id: "analysis.hint.damage_taken_per_min.why",
      message:
        "You often stand in the open or enter fights first without support. That means extra deaths and retreats.",
    }),
    fix: msg({
      id: "analysis.hint.damage_taken_per_min.fix",
      message:
        "Use cover and shoot around corners. Don't go in first unless you are the tank; leave a fight before your health drops to a third.",
    }),
  },
  time_dead_share: {
    meaning: msg({
      id: "analysis.hint.time_dead_share.meaning",
      message: "The part of the match you spent waiting to respawn.",
    }),
    why: msg({
      id: "analysis.hint.time_dead_share.why",
      message:
        "While dead you don't farm or help your team, and enemies get souls for you. Late in the game one death costs over a minute.",
    }),
    fix: msg({
      id: "analysis.hint.time_dead_share.fix",
      message:
        "Don't stay in a fight on low health — retreat earlier. Don't go alone deep into the enemy half without knowing where enemies are.",
    }),
  },
  unspent_souls: {
    meaning: msg({
      id: "analysis.hint.unspent_souls.meaning",
      message: "How long, on average, each soul you earned sat in your pocket before you spent it.",
    }),
    why: msg({
      id: "analysis.hint.unspent_souls.why",
      message: "Souls in your pocket don't fight. Until you buy, you are weaker than you could be.",
    }),
    fix: msg({
      id: "analysis.hint.unspent_souls.fix",
      message:
        "Visit the shop every time you are at base or pass a lane shop. If a big item is out of reach, buy its component or a cheap item now, not five minutes later.",
    }),
  },
  unspent_ap: {
    meaning: msg({
      id: "analysis.hint.unspent_ap.meaning",
      message: "How many ability points, on average, you held without spending.",
    }),
    why: msg({
      id: "analysis.hint.unspent_ap.why",
      message:
        "Every unspent point is an ability weaker than it could be: less damage, longer cooldowns.",
    }),
    fix: msg({
      id: "analysis.hint.unspent_ap.fix",
      message:
        "Spend points as soon as you get them: first in your hero's main ability, then in the order strong players use.",
    }),
  },
};
